import { z } from 'zod'

import { http } from '@/shared/api'

import type { LeadDetail, LeadFilters, LeadListItem, LeadPage } from '../model/types'

const listItemSchema = z.object({
  id: z.string(),
  campaign_id: z.string(),
  type: z.string(),
  page: z.string(),
  block_reason: z.string(),
  source: z.string(),
  ip: z.string(),
  device_type: z.string(),
  device_os: z.string(),
  country: z.string(),
  city: z.string(),
  lang: z.string(),
  created_at: z.string(),
})

const listSchema = z.object({
  leads: z.array(listItemSchema).nullable(),
  total: z.number(),
})

const toListItem = (raw: z.infer<typeof listItemSchema>): LeadListItem => ({
  id: raw.id,
  campaignId: raw.campaign_id,
  type: raw.type,
  page: raw.page,
  blockReason: raw.block_reason,
  source: raw.source,
  ip: raw.ip,
  deviceType: raw.device_type,
  deviceOs: raw.device_os,
  country: raw.country,
  city: raw.city,
  lang: raw.lang,
  createdAt: raw.created_at,
})

/**
 * One page of the log. `total` is the size of the whole selection under the
 * same filters — the server counts it, so the table can say which page it is on
 * instead of guessing from whether the current one came back full.
 *
 * Empty filter values are dropped by the `http` facade, and the backend reads
 * an absent parameter as «не фильтровать».
 */
export async function fetchLeads(
  params: LeadFilters & { campaignId: string; limit: number; offset: number },
  signal?: AbortSignal,
): Promise<LeadPage> {
  const { leads, total } = await http.get(`campaigns/${params.campaignId}/leads`, {
    searchParams: {
      from: params.from,
      to: params.to,
      q: params.q,
      // Comma-joined, one parameter per field. The backend accepts the repeated
      // form too, but `?type=bot,lead` is the one a person can read in the
      // address bar — and an empty list becomes '' and is dropped below.
      type: params.type.join(','),
      served: params.served.join(','),
      country: params.country.join(','),
      source: params.source.join(','),
      limit: params.limit,
      offset: params.offset,
    },
    schema: listSchema,
    signal,
  })

  return { items: (leads ?? []).map(toListItem), total }
}

const detailInnerSchema = listItemSchema.extend({
  user_id: z.string(),
  domain: z.string(),
  uri: z.string(),
  isp: z.string(),
  is_vpn: z.boolean(),
  user_agent: z.string(),
  referer: z.string(),
  dns: z.string(),
  utms: z.array(z.string()).nullable(),
  accept_language: z.string(),
  x_forwarded_for: z.string(),
  sec_ch_ua: z.string(),
  sec_ch_ua_mobile: z.string(),
  sec_ch_ua_platform: z.string(),
  sec_fetch_site: z.string(),
  sec_fetch_mode: z.string(),
  sec_fetch_dest: z.string(),
})

const detailSchema = z.object({ lead: detailInnerSchema })

export async function fetchLead(id: string, signal?: AbortSignal): Promise<LeadDetail> {
  const { lead } = await http.get(`leads/${id}`, { schema: detailSchema, signal })

  return {
    ...toListItem(lead),
    userId: lead.user_id,
    domain: lead.domain,
    uri: lead.uri,
    isp: lead.isp,
    isVpn: lead.is_vpn,
    userAgent: lead.user_agent,
    referer: lead.referer,
    dns: lead.dns,
    utms: lead.utms ?? [],
    acceptLanguage: lead.accept_language,
    xForwardedFor: lead.x_forwarded_for,
    secChUa: lead.sec_ch_ua,
    secChUaMobile: lead.sec_ch_ua_mobile,
    secChUaPlatform: lead.sec_ch_ua_platform,
    secFetchSite: lead.sec_fetch_site,
    secFetchMode: lead.sec_fetch_mode,
    secFetchDest: lead.sec_fetch_dest,
  }
}
